import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { AuthGuard } from './core/auth/auth.guard';
import { AdminGuard } from './core/guard/admin.guard';
import { MiniTenderModuleGuard } from './core/guard/mini-tender-module.guard';
import { PublicOfferGuard } from './core/guard/publicOffer.guard';
import { AzureCallbackComponent } from './shared/azure-callback/azure-callback.component';
import {
  AccountRouteNames,
  AdminRouteNames,
  ContractRouteNames,
  DemandRequestRouteNames,
  HomeRouteNames,
  OfferRouteNames,
  TenderRouteNames,
} from './shared/routing.constants';

export const appRoutes: Routes = [
  {
    path: 'azure/loginCallback',
    component: AzureCallbackComponent,
    data: {
      breadcrumb: _('AZURE_CALLBACK.TITLE'),
    },
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
    data: {
      breadcrumb: {
        info: 'home',
      },
    },
  },
  {
    path: 'help',
    loadChildren: () => import('./ajs/help.routing').then(m => m.helpRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'appstore',
    loadChildren: () =>
      import('./ajs/appstore.routing').then(m => m.appStoreRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'catalog',
    loadChildren: () =>
      import('./ajs/catalog.routing').then(m => m.catalogRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'invitation',
    loadChildren: () =>
      import('./ajs/invitation.routing').then(m => m.invitationRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'supply_incident_overview',
    loadChildren: () =>
      import('./ajs/supply-incident.routing').then(m => m.supplyRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'second_market',
    loadChildren: () =>
      import('./ajs/second-market.routing').then(m => m.secondRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: 'legacy',
    loadChildren: () =>
      import('./ajs/legacy.routing').then(m => m.legacyRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: AccountRouteNames.ModuleBase,

    loadChildren: () =>
      import('./account/account.routing').then(m => m.accountRoutes),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: HomeRouteNames.ModuleBase,
    loadChildren: () => import('./home/home.routing').then(m => m.homerRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () => import('./ajs/offer.routing').then(m => m.offerRoutes),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: '',
    loadChildren: () => import('./ajs/gpo.routing').then(m => m.gpoRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () =>
      import('./ajs/analysis.routing').then(m => m.analysisRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () =>
      import('./ajs/company.routing').then(m => m.companyRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: ContractRouteNames.ModuleBase,
    loadChildren: () =>
      import('./contract/contract.routing').then(m => m.contractRoutes),
    data: {
      breadcrumb: _('CONTRACT.TITLE'),
    },
    canActivate: [AuthGuard, MiniTenderModuleGuard],
  },
  {
    path: '',
    loadChildren: () =>
      import('./ajs/contract.routing').then(m => m.contractRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () =>
      import('./ajs/master-data.routing').then(m => m.masterDataRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () => import('./ajs/order.routing').then(m => m.orderRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () =>
      import('./ajs/request.routing').then(m => m.requestRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: DemandRequestRouteNames.ModuleBase,
    loadChildren: () =>
      import('./demand-request/demand-request.routing').then(
        m => m.demandRoutes
      ),
    data: {
      breadcrumb: _('DEMAND_REQUEST.TITLE'),
    },
    canActivate: [AuthGuard],
  },
  {
    path: '',
    loadChildren: () =>
      import('./ajs/buyer-request.routing').then(m => m.buyerRequestRoutes),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: '',
    loadChildren: () => import('./ajs/srm.routing').then(m => m.srmRoutes),
    data: {
      breadcrumb: { skip: true },
    },
    canActivate: [AuthGuard],
  },
  {
    path: AdminRouteNames.ModuleBase,
    loadChildren: () =>
      import('./admin/admin.routing').then(m => m.adminRoutes),
    data: {
      breadcrumb: _('ADMIN_DASHBOARD.TITLE'),
    },
    canActivate: [AuthGuard, AdminGuard],
  },

  {
    path: TenderRouteNames.ModuleBase,
    loadChildren: () =>
      import('./tender/tender.routing').then(m => m.tenderRoutes),
    data: {
      breadcrumb: _('TENDER.TITLE'),
    },
    canActivate: [AuthGuard],
  },
  // Public Offer
  {
    path: `${OfferRouteNames.ModuleBase}/${OfferRouteNames.Public}/:${OfferRouteNames.TenderOid}`,
    loadComponent: () =>
      import('./offer/offer-public/offer-public.component').then(
        m => m.OfferPublicComponent
      ),
    canActivate: [PublicOfferGuard],
  },
  {
    path: OfferRouteNames.ModuleBase,
    loadChildren: () =>
      import('./offer/offer.routing').then(m => m.offerRoutes),
    data: {
      breadcrumb: _('OFFER.TITLE'),
    },
    canActivate: [AuthGuard],
  },

  {
    // Needed for handling redirect after login
    path: 'auth',
    component: MsalRedirectComponent,
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes)],
  exports: [RouterModule],
})
export class AppRouting {}
